import { useState } from "react"

import { useTranslation } from "react-i18next"
import { Button, Checkbox, Dropdown, Form, Icon, Modal } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"
import { RichText } from "simplydo/react/richtext"

import { TooltipButton } from "components/lib/UI"

export const QuestionModal = ({
  question,
  onClose,
  canManageQuestions,
  saveAndMakePublic,
  createQuestionMutation,
  updateQuestionMutation,
  publishQuestionMutation,
}: {
  question: OpenAPI.Schemas["Question"]
  onClose: () => void
  canManageQuestions: boolean
  saveAndMakePublic: (question: OpenAPI.Schemas["Question"], notifyUsers: boolean) => void
  createQuestionMutation: any
  updateQuestionMutation: any
  publishQuestionMutation: any
}) => {
  const { t } = useTranslation()
  const [questionData, setQuestionData] = useState(question)
  const [hasConfirmed, setHasConfirmed] = useState(canManageQuestions)

  return (
    <Modal open={!!question} mountNode={document.getElementById("semantic-modal-mount-node")}>
      <Modal.Header>{question._id ? "Edit Question" : "Ask a new question"}</Modal.Header>.
      <Modal.Content>
        {!canManageQuestions ? (
          <>
            <div style={{ marginBottom: 16 }}>
              Before you ask a question, please check if it has already been asked and answered. If you have a new
              question, please provide as much context as possible to give the {t("generic.challenge")} managers the
              best chance of answering your question.
            </div>
            <Checkbox
              toggle
              checked={hasConfirmed}
              onChange={(_e, data) => {
                setHasConfirmed(data.checked)
              }}
              label="I confirm that I have read the current list of clarification questions and responses, and that my question is new."
              style={{ marginBottom: 16 }}
            />
          </>
        ) : null}
        <Form>
          <Form.TextArea
            label="Question"
            placeholder={`In detail, what would you like an explanation for? Please provide as much context as possible to give ${t("generic.challenge")} managers the best chance of answering your question.`}
            value={questionData.question}
            onChange={(e) => {
              setQuestionData((prev) => ({ ...prev, question: e.target.value }))
            }}
          />
          {canManageQuestions ? (
            <>
              <h5>Answer (only available to {t("generic.challenge")} managers)</h5>
              <RichText
                autoHideMenu={false}
                forType="challenge"
                forId={question.challenge}
                emptyReturnValue={""}
                placeholder="Please provide an answer to the question above."
                value={questionData.answer}
                onChange={(data) => {
                  setQuestionData((prev) => ({ ...prev, answer: data }))
                }}
              />
            </>
          ) : null}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => onClose()}>Close</Button>
        <Button
          primary={!canManageQuestions}
          disabled={!hasConfirmed}
          loading={createQuestionMutation.isPending || updateQuestionMutation.isPending}
          onClick={() => {
            if (question._id) {
              updateQuestionMutation.mutate({
                params: [
                  question.challenge,
                  question._id,
                  {
                    question: questionData.question,
                    answer: questionData.answer,
                  },
                ],
              })
            } else {
              createQuestionMutation.mutate({
                params: [
                  question.challenge,
                  {
                    question: questionData.question,
                    answer: questionData.answer,
                  },
                ],
              })
            }
            onClose()
          }}
        >
          <Icon name="save" />
          {canManageQuestions ? "Save" : "Ask question"}
        </Button>
        {canManageQuestions && (
          <Dropdown
            icon={null}
            trigger={
              <TooltipButton
                primary
                style={{ marginLeft: 10 }}
                tooltip={!questionData?.answer && "A question requires an answer in order to be made public."}
                disabled={!hasConfirmed || !questionData?.answer}
                loading={
                  createQuestionMutation.isPending ||
                  updateQuestionMutation.isPending ||
                  publishQuestionMutation.isPending
                }
                icon="paper plane"
                content="Save and make public"
              />
            }
          >
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => saveAndMakePublic(questionData, true)}>
                <Icon name="bell" />
                Make public and notify users
              </Dropdown.Item>
              <Dropdown.Item onClick={() => saveAndMakePublic(questionData, false)}>
                <Icon name="bell slash" />
                Make public without notifying users
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </Modal.Actions>
    </Modal>
  )
}
